import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import LinearProgress from "@mui/material/LinearProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import FitnessCenterRoundedIcon from "@mui/icons-material/FitnessCenterRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import AirRoundedIcon from "@mui/icons-material/AirRounded";
import HubRoundedIcon from "@mui/icons-material/HubRounded";
import DeviceHubRoundedIcon from "@mui/icons-material/DeviceHubRounded";
import ScienceRoundedIcon from "@mui/icons-material/ScienceRounded";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { store } from "../../redux/store";
import {
  GetQuizResultByDateCall,
  GetQuizTitlesCall,
} from "../../redux/api/quizTitle.api";
import {
  listAssignedQuizzesCall,
  startAttemptCall,
  getAttemptCall,
  submitAttemptCall,
  getAttemptResultCall,
  listMyAttemptsCall,
} from "../../redux/api/quizSystem.api";

const formatTime = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const TITLE_SECTIONS = [
  {
    type: "histology",
    title: "Histology",
    blurb: "Cells, tissues, and structures.",
  },
  {
    type: "embryology",
    title: "Embryology",
    blurb: "Embryonic development.",
  },
  {
    type: "anatomy",
    title: "Anatomy — mock text",
    blurb: "Multiple-choice questions on bones, muscles, and regions.",
  },
];

const QUIZ_NOTES = {
  "epithelial tissue": "Covering, lining, and secretion.",
  "connective tissue": "Support and structural framework.",
  bone: "Mineralized tissue and structure.",
  cartilage: "Supportive connective tissue.",
  blood: "Cells and liquid connective tissue.",
  "macular tissue": "Specialized sensory tissue.",
  "nervous system": "Control and communication.",
  skin: "Integrated organ system.",
  "lymphoid organ": "Immune function and defense.",
  "digestive system": "Breakdown and absorption.",
  "urinary system": "Filtration and fluid balance.",
  "respiratory system": "Gas exchange and breathing.",
  "genital system": "Reproductive structures.",
  "endocrine organs": "Hormones and regulation.",
  "sense organ": "Sight, hearing, and touch.",
};

const IMAGE_WELLS = ["#fde8ef", "#efe7ff", "#fff4d6", "#e7f6ef", "#fde8e8", "#e8f1ff", "#fff1e6", "#e7f3ff"];

const noteForTitle = (title) => QUIZ_NOTES[String(title || "").trim().toLowerCase()] || "";

const BODY_QUIZZES = [
  { key: "", title: "Full body", blurb: "All systems on the 3D model.", tint: "#dbeafe", accent: "#2563eb", Icon: AccessibilityNewRoundedIcon, image: "/images/quiz-body/full-body.png" },
  { key: "skeletal", title: "Skeleton", blurb: "Bones", tint: "#f1f5f9", accent: "#64748b", Icon: AccessibilityNewRoundedIcon, image: "/images/quiz-body/skeleton.png" },
  { key: "muscular", title: "Muscles", blurb: "Muscular system", tint: "#fee2e2", accent: "#e11d48", Icon: FitnessCenterRoundedIcon, image: "/images/quiz-body/muscles.png" },
  { key: "nervous", title: "Nerves", blurb: "Nervous system", tint: "#fef3c7", accent: "#d97706", Icon: PsychologyRoundedIcon, image: "/images/quiz-body/nerves.png" },
  { key: "cardiovascular", title: "Heart & vessels", blurb: "Cardiovascular system", tint: "#ffe4e6", accent: "#e11d48", Icon: FavoriteRoundedIcon, image: "/images/quiz-body/heart.png" },
  { key: "organs", title: "Organs", blurb: "Internal organs", tint: "#ffedd5", accent: "#ea580c", Icon: AirRoundedIcon, image: "/images/quiz-body/organs.png" },
  { key: "joints", title: "Joints", blurb: "Joints and ligaments", tint: "#e0e7ff", accent: "#4f46e5", Icon: DeviceHubRoundedIcon, image: "/images/quiz-body/joints.png" },
  { key: "lymphatic", title: "Lymph", blurb: "Lymphatic system", tint: "#dcfce7", accent: "#16a34a", Icon: HubRoundedIcon, image: "/images/quiz-body/lymph.png" },
];

const startPillSx = {
  mt: 1.25,
  alignSelf: "flex-start",
  width: "fit-content",
  border: 0,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.15,
  borderRadius: 999,
  px: 1.5,
  py: 0.65,
  color: "#fff",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: 1,
  background: "#2563eb",
  boxShadow: "none",
};

export default function StudentQuizzes() {
  const navigate = useNavigate();
  const lang = store.getState().general?.language || "en";
  const [assigned, setAssigned] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [titlesByType, setTitlesByType] = useState({
    histology: [],
    embryology: [],
    anatomy: [],
  });
  const [loading, setLoading] = useState(true);
  const [quizQuery, setQuizQuery] = useState("");
  const [currentAttempt, setCurrentAttempt] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [result, setResult] = useState(null);
  const [resultDialog, setResultDialog] = useState(false);
  const [historyDialog, setHistoryDialog] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [quizHistory, setQuizHistory] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const answersRef = React.useRef({});
  answersRef.current = answers;

  const loadAssigned = async () => {
    const res = await listAssignedQuizzesCall();
    setAssigned(Array.isArray(res?.data) ? res.data : []);
  };

  const loadAttempts = async () => {
    const res = await listMyAttemptsCall();
    setAttempts(Array.isArray(res?.data) ? res.data : []);
  };

  const loadQuizTitles = async () => {
    const types = TITLE_SECTIONS.map((section) => section.type);
    const results = await Promise.all(
      types.map((type) => GetQuizTitlesCall({ lang, type }))
    );
    const next = { histology: [], embryology: [], anatomy: [] };
    types.forEach((type, index) => {
      const res = results[index];
      next[type] = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
    });
    setTitlesByType(next);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAssigned(), loadAttempts(), loadQuizTitles()]).finally(() =>
      setLoading(false)
    );
  }, []);

  const startQuiz = async (quizAssignmentId) => {
    setLoading(true);
    const res = await startAttemptCall(quizAssignmentId);
    const data = res?.data;
    if (data?.id) {
      setCurrentAttempt(data);
      setCurrentQuestionIndex(0);
      setAnswers({});
      setTimeLeft((data.time_limit_minutes || 30) * 60);
      setResult(null);
    }
    setLoading(false);
  };

  const getInProgressAttempt = (quizId) =>
    attempts.find((a) => a.quiz_assignment_id === quizId && a.status === "in_progress");

  const submitCurrentAttempt = React.useCallback(async () => {
    if (!currentAttempt?.id || submitting) return;
    setSubmitting(true);
    const answerList = Object.entries(answersRef.current).map(([question_bank_id, selected_answer_key]) => ({
      question_bank_id,
      selected_answer_key,
    }));
    const res = await submitAttemptCall(currentAttempt.id, answerList);
    const data = res?.data;
    setResult(data);
    setResultDialog(true);
    setCurrentAttempt(null);
    setSubmitting(false);
    loadAttempts();
  }, [currentAttempt?.id, submitting]);

  useEffect(() => {
    if (!currentAttempt || timeLeft === null || timeLeft <= 0) return;
    const t = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          setSubmitting(true);
          const answerList = Object.entries(answersRef.current).map(([question_bank_id, selected_answer_key]) => ({
            question_bank_id,
            selected_answer_key,
          }));
          submitAttemptCall(currentAttempt.id, answerList).then((res) => {
            setResult(res?.data);
            setResultDialog(true);
            setCurrentAttempt(null);
            setSubmitting(false);
            loadAttempts();
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [currentAttempt?.id, timeLeft]);

  const handleSelectAnswer = (questionBankId, key) => {
    setAnswers((prev) => ({ ...prev, [questionBankId]: key }));
  };

  const handleSubmit = async () => {
    await submitCurrentAttempt();
  };

  const viewResult = async (attemptId) => {
    const res = await getAttemptResultCall(attemptId);
    setResult(res?.data);
    setResultDialog(true);
  };

  const resumeAttempt = async (attemptId) => {
    const res = await getAttemptCall(attemptId);
    const data = res?.data;
    if (data?.id) {
      setCurrentAttempt(data);
      setCurrentQuestionIndex(0);
      const started = new Date(data.started_at).getTime();
      const limitSec = (data.time_limit_minutes || 30) * 60;
      const elapsed = Math.floor((Date.now() - started) / 1000);
      setTimeLeft(Math.max(0, limitSec - elapsed));
      setAnswers({});
    }
  };

  const loadLegacyQuizHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await GetQuizResultByDateCall();
      const grouped = res?.data && typeof res.data === "object" ? res.data : {};
      const flat = Object.entries(grouped).flatMap(([date, list]) => {
        const arr = Array.isArray(list) ? list : [];
        return arr.map((it) => {
          const row = it?.reservation != null ? it.reservation : it;
          return { ...row, date };
        });
      });
      flat.sort((a, b) => {
        const tA = new Date(a?.created_at || a?.date || 0).getTime();
        const tB = new Date(b?.created_at || b?.date || 0).getTime();
        return tB - tA;
      });
      setQuizHistory(flat);
    } finally {
      setHistoryLoading(false);
    }
  };

  if (currentAttempt) {
    const questions = currentAttempt.questions || [];
    const total = questions.length;
    const current = questions[currentQuestionIndex];
    const progress = total ? ((currentQuestionIndex + 1) / total) * 100 : 0;

    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Card sx={{ p: 2 }}>
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <MDTypography variant="h6">{currentAttempt.quiz_title || "Quiz"}</MDTypography>
              <MDTypography variant="button" color={timeLeft <= 60 ? "error" : "text"}>
                Time left: {formatTime(timeLeft)}
              </MDTypography>
            </MDBox>
            <LinearProgress value={progress} color="info" sx={{ mb: 2 }} />
            <MDTypography variant="caption">Question {currentQuestionIndex + 1} of {total}</MDTypography>

            {current && (
              <MDBox mt={2}>
                <MDTypography variant="body1" fontWeight="medium" sx={{ mb: 2 }}>
                  {current.question_content}
                </MDTypography>
                <FormControl component="fieldset">
                  <RadioGroup
                    value={answers[current.question_bank_id] || ""}
                    onChange={(e) => handleSelectAnswer(current.question_bank_id, e.target.value)}
                  >
                    {(current.answers || []).map((a) => (
                      <FormControlLabel
                        key={a.key}
                        value={a.key}
                        control={<Radio />}
                        label={a.text}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </MDBox>
            )}

            <MDBox mt={3} display="flex" justifyContent="space-between">
              <Button
                disabled={currentQuestionIndex === 0}
                onClick={() => setCurrentQuestionIndex((i) => i - 1)}
              >
                Previous
              </Button>
              {currentQuestionIndex < total - 1 ? (
                <Button variant="contained" onClick={() => setCurrentQuestionIndex((i) => i + 1)}>
                  Next
                </Button>
              ) : (
                <MDButton variant="gradient" color="info" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Submitting..." : "Submit Quiz"}
                </MDButton>
              )}
            </MDBox>
          </Card>
        </MDBox>

        <Dialog open={resultDialog} onClose={() => setResultDialog(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}>
          <DialogTitle>Quiz Result</DialogTitle>
          <DialogContent>
            {result && (
              <MDBox>
                <MDTypography>Score: {result.score}%</MDTypography>
                <MDTypography>Correct: {result.correct_count} / {result.total_questions}</MDTypography>
              </MDBox>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResultDialog(false)}>Close</Button>
            {result?.id && (
              <Button onClick={() => { setResultDialog(false); viewResult(result.id); }}>
                View details
              </Button>
            )}
          </DialogActions>
        </Dialog>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            border: "1px solid rgba(148,163,184,0.22)",
            boxShadow: "0 18px 40px rgba(15, 23, 42, 0.06)",
            backgroundColor: "#fff",
          }}
        >
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            sx={{
              position: "relative",
              overflow: "hidden",
              px: { xs: 2, sm: 3.5 },
              py: 3,
              background: "linear-gradient(180deg, #f8fbff 0%, #eef4ff 100%)",
              borderBottom: "1px solid rgba(148,163,184,0.16)",
            }}
          >
            <Box aria-hidden sx={{ position: "absolute", right: 150, top: -30, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(191,219,254,0.45)" }} />
            <Box aria-hidden sx={{ position: "absolute", right: 40, bottom: -36, width: 90, height: 90, borderRadius: "50%", bgcolor: "rgba(219,234,254,0.8)" }} />
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                right: { xs: 16, sm: 210 },
                top: 18,
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#fff",
                display: { xs: "none", md: "grid" },
                placeItems: "center",
                boxShadow: "0 8px 20px rgba(37,99,235,0.08)",
                color: "#60a5fa",
              }}
            >
              <ScienceRoundedIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <MDTypography sx={{ fontSize: 11, letterSpacing: 1.6, fontWeight: 800, color: "#6366f1" }}>
                PRACTICE
              </MDTypography>
              <MDTypography variant="h4" fontWeight="bold" sx={{ color: "#0f172a", letterSpacing: "-0.04em", lineHeight: 1.05 }}>
                My Quizzes
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
                Practice histology, embryology, anatomy text, and the full-body model.
              </MDTypography>
            </Box>
            <MDButton
              variant="outlined"
              color="info"
              size="small"
              onClick={async () => {
                setHistoryDialog(true);
                await loadLegacyQuizHistory();
              }}
              sx={{
                position: "relative",
                zIndex: 1,
                borderRadius: 999,
                px: 1.6,
                py: 0.8,
                bgcolor: "#fff",
                borderColor: "#bfdbfe",
                color: "#2563eb",
                fontWeight: 700,
              }}
              startIcon={<HistoryRoundedIcon sx={{ fontSize: 16 }} />}
              endIcon={<ChevronRightRoundedIcon sx={{ fontSize: 16 }} />}
            >
              Quiz History
            </MDButton>
          </MDBox>

          <Box sx={{ p: { xs: 1.5, sm: 2.5 }, backgroundColor: "#f8fafc" }}>
        {loading ? (
          <MDTypography>Loading...</MDTypography>
        ) : (
          <MDBox>
            <MDBox mb={3.5}>
              <Box display="flex" alignItems="center" gap={1}>
                <Box sx={{ width: 4, height: 18, borderRadius: 99, bgcolor: "#2563eb" }} />
                <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#0f172a", letterSpacing: "-0.03em" }}>
                  Anatomy on the full body
                </MDTypography>
              </Box>
              <MDTypography variant="caption" sx={{ color: "#64748b", display: "block", mb: 1.5, ml: 1.5 }}>
                Name the structure highlighted on the 3D model.
              </MDTypography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(4, minmax(0, 1fr))",
                  },
                  gap: 1.5,
                }}
              >
                {BODY_QUIZZES.map((topic) => {
                  const TopicIcon = topic.Icon;
                  return (
                  <Card
                    key={topic.title}
                    sx={{
                      position: "relative",
                      overflow: "hidden",
                      minHeight: 168,
                      p: 2,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.9)",
                      backgroundColor: "#fff",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
                      cursor: "pointer",
                      transition: "transform .18s ease, box-shadow .18s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
                      },
                    }}
                    onClick={() =>
                      navigate(
                        topic.key
                          ? `/anatomy-explorer?quiz=1&systems=${topic.key}`
                          : "/anatomy-explorer?quiz=1"
                      )
                    }
                  >
                    <Box sx={{ position: "relative", zIndex: 1, maxWidth: "62%" }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: 2,
                          bgcolor: topic.tint,
                          color: topic.accent,
                          display: "grid",
                          placeItems: "center",
                          mb: 1,
                        }}
                      >
                        <TopicIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <MDTypography fontWeight="bold" sx={{ color: "#0f172a", fontSize: "0.98rem", letterSpacing: "-0.02em" }}>
                        {topic.title}
                      </MDTypography>
                      <MDTypography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.25 }}>
                        {topic.blurb}
                      </MDTypography>
                      <Box component="span" sx={{ ...startPillSx, pointerEvents: "none" }}>
                        Start Quiz
                        <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
                      </Box>
                    </Box>
                    <Box
                      aria-hidden
                      sx={{
                        position: "absolute",
                        right: -8,
                        bottom: -12,
                        width: 132,
                        height: 150,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        pointerEvents: "none",
                      }}
                    >
                      {topic.image ? (
                        <Box component="img" src={topic.image} alt="" sx={{ height: "108%", width: "100%", objectFit: "contain", objectPosition: "center bottom" }} />
                      ) : (
                        <TopicIcon sx={{ fontSize: 108, color: topic.accent, opacity: 0.18 }} />
                      )}
                    </Box>
                  </Card>
                  );
                })}
              </Box>
            </MDBox>

            {TITLE_SECTIONS.map((section, sectionIndex) => {
              const query = quizQuery.trim().toLowerCase();
              const list = (titlesByType[section.type] || []).filter((item) =>
                !query || String(item.title || "").toLowerCase().includes(query)
              );
              return (
                <MDBox key={section.type} mb={3.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={2} flexWrap="wrap" mb={1.5}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 4, height: 18, borderRadius: 99, bgcolor: "#2563eb" }} />
                        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#0f172a", letterSpacing: "-0.03em" }}>
                          {section.title}
                        </MDTypography>
                      </Box>
                      <MDTypography variant="caption" sx={{ color: "#64748b", display: "block", mt: 0.3, ml: 1.5 }}>
                        {section.blurb}
                      </MDTypography>
                    </Box>
                    {sectionIndex === 0 ? (
                      <TextField
                        value={quizQuery}
                        onChange={(event) => setQuizQuery(event.target.value)}
                        placeholder="Search quizzes..."
                        size="small"
                        sx={{
                          minWidth: 220,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 999,
                            bgcolor: "#fff",
                            fontSize: 14,
                          },
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchRoundedIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : null}
                  </Box>
                  {list.length === 0 ? (
                    <MDTypography color="text" sx={{ mb: 1 }}>
                      No quizzes in this section yet.
                    </MDTypography>
                  ) : (
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "1fr",
                          sm: "repeat(2, minmax(0, 1fr))",
                          md: "repeat(3, minmax(0, 1fr))",
                          lg: "repeat(5, minmax(0, 1fr))",
                        },
                        gap: 1.5,
                      }}
                    >
                      {list.map((q, index) => {
                        const note = noteForTitle(q.title);
                        return (
                  <Card
                    key={q.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 3,
                      border: "1px solid rgba(226,232,240,0.9)",
                      backgroundColor: "#fff",
                      boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
                      transition: "transform .18s ease, box-shadow .18s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: "0 16px 36px rgba(15, 23, 42, 0.08)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 112,
                        borderRadius: 2.5,
                        overflow: "hidden",
                        bgcolor: IMAGE_WELLS[index % IMAGE_WELLS.length],
                        mb: 1.25,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {q.image ? (
                        <Box
                          component="img"
                          src={q.image}
                          alt={q.title || "Quiz"}
                          sx={{ width: "72%", height: "72%", objectFit: "contain" }}
                        />
                      ) : (
                        <MDTypography variant="caption" color="text">
                          No image
                        </MDTypography>
                      )}
                    </Box>
                    <MDTypography
                      fontWeight="bold"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        px: 0.3,
                        fontSize: "0.95rem",
                        lineHeight: 1.25,
                        color: "#0f172a",
                        letterSpacing: "-0.02em",
                      }}
                    >
                      {q.title || "Quiz"}
                    </MDTypography>
                    {note ? (
                      <MDTypography variant="caption" sx={{ display: "block", color: "#64748b", px: 0.3, mt: 0.35, minHeight: 32 }}>
                        {note}
                      </MDTypography>
                    ) : (
                      <Box sx={{ minHeight: 32 }} />
                    )}
                    <Box
                      component="button"
                      type="button"
                      sx={startPillSx}
                      onClick={() => navigate(`/showQuestions/${q.id}/${lang}`)}
                    >
                      Start Quiz
                      <ChevronRightRoundedIcon sx={{ fontSize: 16 }} />
                    </Box>
                  </Card>
                        );
                      })}
                    </Box>
                  )}
                </MDBox>
              );
            })}

            {assigned.length > 0 ? (
              <MDBox mt={1}>
                <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0f172a", mb: 1.5, display: "block" }}>
                  Assigned to you
                </MDTypography>
              {assigned.map((q) => {
                const inProgress = getInProgressAttempt(q.id);
                return (
                  <Card key={q.id} sx={{ p: 2, mb: 1.5, borderRadius: 3, border: "1px solid rgba(148,163,184,0.22)", boxShadow: "none" }}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <MDBox>
                        <MDTypography variant="h6">{q.title}</MDTypography>
                        <MDTypography variant="caption">Time limit: {q.time_limit_minutes} min</MDTypography>
                      </MDBox>
                      <MDBox>
                        {inProgress ? (
                          <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => resumeAttempt(inProgress.id)}
                            sx={{ borderRadius: 999 }}
                          >
                            Continue Quiz
                          </MDButton>
                        ) : (
                          <MDButton
                            variant="gradient"
                            color="info"
                            onClick={() => startQuiz(q.id)}
                            sx={{ borderRadius: 999 }}
                          >
                            Start Quiz
                          </MDButton>
                        )}
                      </MDBox>
                    </MDBox>
                  </Card>
                );
              })}
              </MDBox>
            ) : null}
          </MDBox>
        )}

        {attempts.filter((a) => a.status === "submitted").length > 0 && (
          <MDBox mt={3}>
            <MDTypography variant="button" fontWeight="bold" sx={{ color: "#0f172a", mb: 1.5, display: "block" }}>Past attempts</MDTypography>
            {attempts.filter((a) => a.status === "submitted").slice(0, 10).map((a) => (
              <Card key={a.id} sx={{ p: 2, mb: 1, borderRadius: 3, border: "1px solid rgba(148,163,184,0.22)", boxShadow: "none" }}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography>{a.quizAssignment?.title || "Quiz"} — Score: {a.score}%</MDTypography>
                  <Button size="small" onClick={() => viewResult(a.id)}>View result</Button>
                </MDBox>
              </Card>
            ))}
          </MDBox>
        )}
          </Box>
        </Card>
      </MDBox>
      <Dialog
        open={historyDialog}
        onClose={() => setHistoryDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "background.card", color: "text.main" } }}
      >
        <DialogTitle>Quiz History</DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <MDTypography variant="body2" color="text">
              Loading...
            </MDTypography>
          ) : quizHistory.length === 0 ? (
            <MDTypography variant="body2" color="text">
              No quiz history yet.
            </MDTypography>
          ) : (
            quizHistory.slice(0, 20).map((a, idx) => (
                <Card key={a?.resultId || a?.id || idx} sx={{ p: 1.5, mb: 1.2 }}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <MDTypography variant="button">
                      {a?.title || "Quiz"}
                    </MDTypography>
                    <MDTypography variant="caption" fontWeight="bold">
                      {a?.score ?? 0}
                    </MDTypography>
                  </MDBox>
                  <MDTypography variant="caption" color="text">
                    {a?.created_at || a?.date
                      ? new Date(a.created_at || a.date).toLocaleString()
                      : ""}
                  </MDTypography>
                </Card>
              ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}
